import { createEntityAdapter, createSlice, createSelector } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import { of } from "rxjs";
import { mergeMap, map, catchError } from "rxjs/operators";
import { calculatePossibleWinningsFull, calculatePossibleWinningsGain, calculatePossibleWinningsBase } from "@nxplatform/helpers/selectors/math-selectors";
import { BonusFreeBetTypes } from "@nxplatform/platform-entities/sport/common-sport.entities";
const BETSLIP_FREEBETS_FEATURE_KEY = "betslipFreebets";
var FreeBetIdActs = /* @__PURE__ */ ((FreeBetIdActs2) => {
  FreeBetIdActs2["add"] = "add";
  FreeBetIdActs2["remove"] = "remove";
  return FreeBetIdActs2;
})(FreeBetIdActs || {});
var FreeBetType = /* @__PURE__ */ ((FreeBetType2) => {
  FreeBetType2["FREEBET"] = "freebet";
  FreeBetType2["RISKBET"] = "riskbet";
  return FreeBetType2;
})(FreeBetType || {});
const FreeBetName = "FREE BET";
const FreeBetTypeParams = {
  [
    "freebet"
    /* FREEBET */
  ]: "freebetId",
  [
    "riskbet"
    /* RISKBET */
  ]: "riskFreebetId"
};
const possibleFreeBetWinByType = (type, amount, totalOdds) => {
  const mapped = {
    [BonusFreeBetTypes.Base]: calculatePossibleWinningsBase,
    [BonusFreeBetTypes.Gain]: calculatePossibleWinningsGain,
    [BonusFreeBetTypes.Full]: calculatePossibleWinningsFull
  };
  try {
    return mapped[type](amount, totalOdds);
  } catch (e) {
    console.info("#missed type#", type);
    return mapped[BonusFreeBetTypes.Full](amount, totalOdds);
  }
};
const groupFreeBetItems = (freeBets, betType) => freeBets.reduce((acc, objectItem) => {
  const keyItem = objectItem.id;
  Object.assign(acc, {
    [keyItem]: {
      ...objectItem,
      betType
    }
  });
  return acc;
}, /* @__PURE__ */ Object.create(null));
const betslipFreebetsAdapter = createEntityAdapter();
const initialBetslipFreebetsState = betslipFreebetsAdapter.getInitialState({
  loadingStatus: "not loaded",
  error: null,
  entities: /* @__PURE__ */ Object.create(null),
  selected: /* @__PURE__ */ new Map(),
  riskFreeBets: /* @__PURE__ */ Object.create(null),
  riskBetsLoaded: false
});
const betslipFreebetsSlice = createSlice({
  name: BETSLIP_FREEBETS_FEATURE_KEY,
  initialState: initialBetslipFreebetsState,
  reducers: {
    getFreeBetsStart: () => {
    },
    getFreeBetsSuccess: (state, action) => {
      state.entities = groupFreeBetItems(
        action.payload.items,
        "freebet"
        /* FREEBET */
      );
    },
    getFreeBetsFromSocketsSuccess: (state, action) => {
      const freeBet = { [action.payload.id]: {
        ...action.payload,
        betType: "freebet"
        /* FREEBET */
      } };
      Object.assign(state.entities, freeBet);
    },
    deleteFreeBetSuccess: (state, action) => {
      const { id } = action.payload;
      if (id in state.entities) {
        delete state.entities[id];
      }
    },
    getFreeBetsFailure: (state, action) => {
      state.error = action.payload;
    },
    getActiveFreeBetsStart: () => {
    },
    getActiveFreeBetsSuccess: () => {
    },
    getActiveFreeBetsFailure: (state, action) => {
      state.error = action.payload;
    },
    removeSelectedBet: (state, action) => {
      state.selected.delete(action.payload.betId);
    },
    assignFreeBet: (state, action) => {
      const { betId, freeBet } = action.payload;
      const isActiveFreeBet = [...state.selected.entries()].find(
        (activeFreeBet) => activeFreeBet[1].id === freeBet.id
      );
      if (isActiveFreeBet) {
        state.selected.delete(isActiveFreeBet[0]);
      }
      state.selected.set(betId, freeBet);
    },
    removeAllSelectedFreeBets: (state) => {
      var _a, _b;
      (_b = (_a = state.selected).clear) == null ? void 0 : _b.call(_a);
    },
    getRiskFreeBetsStart: () => {
    },
    getRiskFreeBetsSuccess: (state, action) => {
      state.riskFreeBets = groupFreeBetItems(
        action.payload.items,
        "riskbet"
        /* RISKBET */
      );
      state.riskBetsLoaded = true;
    },
    getRiskFreeBetsFromSocketsSuccess: (state, action) => {
      const freeBet = { [action.payload.id]: {
        ...action.payload,
        betType: "riskbet"
        /* RISKBET */
      } };
      Object.assign(state.riskFreeBets, freeBet);
    },
    deleteRiskFreeBetSuccess: (state, action) => {
      const { id } = action.payload;
      if (id in state.riskFreeBets) {
        delete state.riskFreeBets[id];
      }
    },
    getRiskFreeBetsFailure: () => {
    },
    initialFreeBetState: (state) => {
      state.entities = initialBetslipFreebetsState.entities;
      state.selected = initialBetslipFreebetsState.selected;
    }
  }
});
const betslipFreebetsReducer = betslipFreebetsSlice.reducer;
const {
  removeSelectedBet,
  getFreeBetsStart,
  assignFreeBet,
  getActiveFreeBetsFailure,
  getActiveFreeBetsStart,
  getActiveFreeBetsSuccess,
  removeAllSelectedFreeBets,
  getFreeBetsSuccess,
  deleteFreeBetSuccess,
  getFreeBetsFailure,
  getFreeBetsFromSocketsSuccess,
  getRiskFreeBetsFromSocketsSuccess,
  deleteRiskFreeBetSuccess,
  getRiskFreeBetsStart,
  getRiskFreeBetsSuccess,
  getRiskFreeBetsFailure,
  initialFreeBetState
} = betslipFreebetsSlice.actions;
const getBetslipFreebetsState = (rootState) => rootState[BETSLIP_FREEBETS_FEATURE_KEY];
const selectAllBetSlipFreeBets = createSelector(
  getBetslipFreebetsState,
  (s) => s.entities
);
const selectAllBetSlipFreeBetsArray = createSelector(
  getBetslipFreebetsState,
  (s) => Object.values(s.entities || {})
);
const selectSelectedFreeBet = createSelector(
  getBetslipFreebetsState,
  (s) => s.selected
);
const selectIsActiveForBetFreeBet = createSelector(
  selectSelectedFreeBet,
  (selected, betId) => betId,
  (selected, betId) => {
    const { id } = selected.get(betId) || {};
    return id || null;
  }
);
const selectFreeBetById = createSelector(
  getBetslipFreebetsState,
  (s, id) => id,
  ({ entities, riskFreeBets }, id) => entities[id] || riskFreeBets[id] || null
);
const selectRiskFreeBets = createSelector(
  getBetslipFreebetsState,
  ({ riskFreeBets }) => Object.values(riskFreeBets || {})
);
const fetchAppFreeBets = () => ({ type: getFreeBetsStart.type });
const fetchAppFreeBetsEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getFreeBetsStart.type),
  mergeMap(() => Api.betSlip.getActiveFreeBets().pipe(
    map((data) => getFreeBetsSuccess(data)),
    catchError((err) => of(getFreeBetsFailure(err)))
  ))
);
const getRiskFreeBets = () => ({
  type: getRiskFreeBetsStart.type
});
const getRiskFreeBetsEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getRiskFreeBetsStart.type),
  mergeMap(() => Api.betSlip.getRiskFreeBets().pipe(
    map(({ items }) => getRiskFreeBetsSuccess({ items })),
    catchError(() => of(getRiskFreeBetsFailure()))
  ))
);
export {
  BETSLIP_FREEBETS_FEATURE_KEY,
  FreeBetIdActs,
  FreeBetName,
  FreeBetType,
  FreeBetTypeParams,
  assignFreeBet,
  betslipFreebetsAdapter,
  betslipFreebetsReducer,
  betslipFreebetsSlice,
  deleteFreeBetSuccess,
  deleteRiskFreeBetSuccess,
  fetchAppFreeBets,
  fetchAppFreeBetsEpic,
  getActiveFreeBetsFailure,
  getActiveFreeBetsStart,
  getActiveFreeBetsSuccess,
  getBetslipFreebetsState,
  getFreeBetsFailure,
  getFreeBetsFromSocketsSuccess,
  getFreeBetsStart,
  getFreeBetsSuccess,
  getRiskFreeBets,
  getRiskFreeBetsEpic,
  getRiskFreeBetsFailure,
  getRiskFreeBetsFromSocketsSuccess,
  getRiskFreeBetsStart,
  getRiskFreeBetsSuccess,
  groupFreeBetItems,
  initialBetslipFreebetsState,
  initialFreeBetState,
  possibleFreeBetWinByType,
  removeAllSelectedFreeBets,
  removeSelectedBet,
  selectAllBetSlipFreeBets,
  selectAllBetSlipFreeBetsArray,
  selectFreeBetById,
  selectIsActiveForBetFreeBet,
  selectRiskFreeBets,
  selectSelectedFreeBet
};
